import {
  ClipboardCheck,
  AlertCircle,
  RefreshCw,
  UserPlus,
  CheckCircle2,
  ShieldAlert,
  SearchX,
  Clock,
  WifiOff,
  AlertTriangle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * tool name → i18n key for the in-stream "doing X…" label. Shared by
 * StreamingIndicator and InlineToolEvent (keeps both in sync, single source).
 */
const TOOL_STREAMING_I18N: Record<string, string> = {
  create_inspection: "inspectionChat.streaming.creatingInspection",
  create_defect: "inspectionChat.streaming.creatingDefect",
  query_inspections: "inspectionChat.streaming.fetchingData",
  query_defects: "inspectionChat.streaming.fetchingData",
  list_checklists: "inspectionChat.streaming.fetchingData",
  list_team_members: "inspectionChat.streaming.fetchingData",
  save_response: "inspectionChat.streaming.savingResponse",
  complete_inspection: "inspectionChat.streaming.completingInspection",
};

/** Resolve the streaming-label i18n key for a tool (fallback: generic processing). */
export function toolStreamingI18nKey(toolName: string): string {
  return TOOL_STREAMING_I18N[toolName] ?? "inspectionChat.streaming.processing";
}

/**
 * Set of tool names that perform write operations (create/update/complete).
 * These tools are rendered as ActionFeedbackCard instead of ToolBadge pills.
 */
export const WRITE_TOOLS = new Set([
  "create_inspection",
  "create_defect",
  "update_defect",
  "assign_defect",
  "complete_inspection",
  "save_response",
]);

/** tool → (entity key in result.data, route prefix). Drives getEntityRoute. */
const ENTITY_ROUTE_MAP: Record<string, { key: string; prefix: string }> = {
  create_inspection: { key: "inspection", prefix: "/inspections" },
  complete_inspection: { key: "inspection", prefix: "/inspections" },
  create_defect: { key: "defect", prefix: "/defects" },
  update_defect: { key: "defect", prefix: "/defects" },
  assign_defect: { key: "defect", prefix: "/defects" },
};

/** Returns the entity route for a write tool result, or null if no entity link applies. */
export function getEntityRoute(toolName: string, result: Record<string, unknown>): string | null {
  const mapping = ENTITY_ROUTE_MAP[toolName];
  if (!mapping) return null;

  const data = (result.data ?? {}) as Record<string, unknown>;
  const entity = (data[mapping.key] ?? {}) as Record<string, unknown>;
  const id = entity.id;
  return id ? `${mapping.prefix}/${id}` : null;
}

export interface ToolDisplayConfig {
  icon: LucideIcon;
  i18nKey: string;
  borderColor: string;
  badgeColor: string;
}

const TOOL_DISPLAY_CONFIG: Record<string, ToolDisplayConfig> = {
  create_inspection: {
    icon: ClipboardCheck,
    i18nKey: "inspectionChat.actions.inspectionCreated",
    borderColor: "border-l-primary",
    badgeColor: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  },
  create_defect: {
    icon: AlertCircle,
    i18nKey: "inspectionChat.actions.defectCreated",
    borderColor: "border-l-amber-500",
    badgeColor: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  },
  update_defect: {
    icon: RefreshCw,
    i18nKey: "inspectionChat.actions.defectUpdated",
    borderColor: "border-l-blue-500",
    badgeColor: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  },
  assign_defect: {
    icon: UserPlus,
    i18nKey: "inspectionChat.actions.defectAssigned",
    borderColor: "border-l-blue-500",
    badgeColor: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  },
  complete_inspection: {
    icon: CheckCircle2,
    i18nKey: "inspectionChat.actions.inspectionCompleted",
    borderColor: "border-l-emerald-500",
    badgeColor: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  },
  save_response: {
    icon: CheckCircle2,
    i18nKey: "inspectionChat.actions.responseSaved",
    borderColor: "border-l-emerald-500",
    badgeColor: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  },
};

const DEFAULT_TOOL_DISPLAY_CONFIG: ToolDisplayConfig = {
  icon: CheckCircle2,
  i18nKey: "inspectionChat.actions.responseSaved",
  borderColor: "border-l-muted",
  badgeColor: "bg-zinc-500/10 text-zinc-500",
};

/**
 * Returns display config (icon, i18n key, colors) for a write tool.
 */
export function getToolDisplayConfig(toolName: string): ToolDisplayConfig {
  return TOOL_DISPLAY_CONFIG[toolName] ?? DEFAULT_TOOL_DISPLAY_CONFIG;
}

export interface NextStep {
  i18nKey: string;
  action: string;
  actionI18nKey: string;
  actionParams?: Record<string, string>;
}

type NextStepBuilder = (data: Record<string, unknown>) => NextStep;

const VIEW_DETAILS_STEP: NextStep = {
  i18nKey: "inspectionChat.nextSteps.viewDetails",
  action: "Details anzeigen",
  actionI18nKey: "inspectionChat.nextSteps.viewDetailsAction",
};

const NEXT_STEP_BUILDERS: Record<string, NextStepBuilder> = {
  create_inspection: (data) => {
    const inspection = data?.inspection as Record<string, unknown> | undefined;
    const number = String(inspection?.inspection_number ?? "");
    return {
      i18nKey: "inspectionChat.nextSteps.startInspection",
      // `action` is the text re-submitted to the (German-context) agent prompt;
      // the *displayed* label uses actionI18nKey. Kept German + correct umlaut.
      action: `Prüfung ${number} starten`,
      actionI18nKey: "inspectionChat.nextSteps.startInspectionAction",
      actionParams: { number },
    };
  },
  create_defect: (data) => {
    const defect = data?.defect as Record<string, unknown> | undefined;
    const title = String(defect?.title ?? "");
    return {
      i18nKey: "inspectionChat.nextSteps.assignDefect",
      action: `Mangel "${title}" zuweisen`,
      actionI18nKey: "inspectionChat.nextSteps.assignDefectAction",
      actionParams: { title },
    };
  },
  update_defect: () => VIEW_DETAILS_STEP,
  assign_defect: () => VIEW_DETAILS_STEP,
  complete_inspection: () => VIEW_DETAILS_STEP,
};

/**
 * Returns the next-step suggestion for a write tool result, or null if none applies.
 */
export function getNextStep(toolName: string, result: Record<string, unknown>): NextStep | null {
  const builder = NEXT_STEP_BUILDERS[toolName];
  if (!builder) return null;
  return builder((result?.data as Record<string, unknown> | undefined) ?? {});
}

export interface ErrorCategory {
  i18nKey: string;
  icon: LucideIcon;
  borderColor: string;
  alternatives: string[];
}

const ERROR_CATEGORY_MAP: Record<string, ErrorCategory> = {
  permission_denied: {
    i18nKey: "inspectionChat.errors.permissionDenied",
    icon: ShieldAlert,
    borderColor: "border-amber-500",
    alternatives: ["inspectionChat.errors.alternatives.contactAdmin"],
  },
  not_found: {
    i18nKey: "inspectionChat.errors.notFound",
    icon: SearchX,
    borderColor: "border-amber-500",
    alternatives: ["inspectionChat.errors.alternatives.listAvailable"],
  },
  rate_limited: {
    i18nKey: "inspectionChat.errors.rateLimited",
    icon: Clock,
    borderColor: "border-amber-500",
    alternatives: ["inspectionChat.errors.alternatives.waitAndRetry"],
  },
  stream_interrupted: {
    i18nKey: "inspectionChat.errors.streamInterrupted",
    icon: WifiOff,
    borderColor: "border-amber-500",
    alternatives: ["inspectionChat.errors.alternatives.waitAndRetry"],
  },
};

const GENERIC_ERROR_CATEGORY: ErrorCategory = {
  i18nKey: "inspectionChat.errors.generic",
  icon: AlertTriangle,
  borderColor: "border-destructive",
  alternatives: ["inspectionChat.errors.alternatives.trySimpler"],
};

/**
 * Categorizes an error code into a display-ready category with icon, i18n key, and alternatives.
 */
export function categorizeError(errorCode?: string): ErrorCategory {
  return (errorCode && ERROR_CATEGORY_MAP[errorCode]) || GENERIC_ERROR_CATEGORY;
}
